import React, { useEffect } from 'react';
import { isNull } from 'lodash';
import { Global } from '@emotion/react';
import { MuiTheme } from '@theme/mercuryHolidaysTheme';
import StylesProvider from '@mui/styles/StylesProvider';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { globalStyles } from '@theme/base';
import { isServer } from '@util/is-server';
import { GtmBootstrap } from '@model/bootstrap/gtm-bootstrap';
import { PolyfillBootstrap } from '@model/bootstrap/polyfill-bootstrap';
import { reduxStore, useDispatch, useSelector } from '@state/store';
import { getAbta } from '@state/slices/agent/agentSelectors';
import { RouterEventsBootstrap } from '@model/bootstrap/router-events-bootstrap';
import { WithT } from '@components/hoc';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import 'react-image-gallery/styles/css/image-gallery.css';
import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import { PersistGate } from 'redux-persist/integration/react';
import { useRouter } from 'next/router';
import { getPath, initializePath } from '@state/slices/app';
import { PageWrapper } from '@layout/PageWrapper';
import { CookiesBanner } from '@components/common/cookies-banner/CookiesBanner';
import { DevBanner } from '@components/dev-banner/DevBanner';
import { RootModalContainer } from '@components/modal/root/RootModalContainer';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { EnvConfig } from '@model/config/env-config';
import { WithAuth } from '@model/bootstrap/with-auth';
import { getBookingPageFromUrl } from '@model/common/pages/get-booking-page-from-url';
import { EPCModalContainer, PaywallBannerModalContainer } from '@components/modal/promo-banner';
import Cookies, { Cookie } from '@model/common/cookie/cookie';
import { Uuid } from '@model/bootstrap/uuid-bootstrap';
import { Sfsk } from '@model/bootstrap/sfsk-bootstrap';
import { growthbook } from '@model/ab-testing/service/ab-testing-api';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { Epc } from '@model/bootstrap/epc-bootstrap';
import { DevProtect } from '@model/bootstrap/dev-protect';
import locales from '../../public/assets/locales/locales.json';
import { XdebugBootstrap } from '@model/bootstrap/xdebug-bootstrap';
import { useUiState } from '@components/hooks';
import { TalkdeskChatButton } from '@components/chat/talk-desk/TalkdeskChatButton';
import { ConditionalWrapper } from '@util/common';

const TALKDESK_CONFIG = {
  devPointId: 'dbe603f252fc4d6188c3d01303fb5a92',
  prodPointId: '79e665735d0344a882e3ac974800fd25',
  region: 'td-eu-1'
};

export const withGlobalProvider = (Page: any) => {
  return (ownProps: any) => {
    const dispatch = useDispatch();
    const persistor = reduxStore.__persistor;
    const path = useSelector(getPath);
    const abta = useSelector(getAbta);
    const router = useRouter();
    const routerPath = router.asPath;
    const gtmBootstrap = new GtmBootstrap();

    useUiState();

    useEffect(() => {
      if (!path) {
        new XdebugBootstrap().initialize();
      }
      if (isServer) {
        return;
      }
      const routerPath = router.asPath;

      new DevProtect().initialize(abta);
      if (!path) {
        // one time setup
        gtmBootstrap.initialize();
        new Sfsk().initialize();
        new Epc().initialize();
        momentDurationFormatSetup(moment as any);
        new PolyfillBootstrap().initialize();
        new WithAuth().initialize();
        new RouterEventsBootstrap().initialize({
          dispatch
        });
        const uuid = new Uuid();
        uuid.initialize();
        if (growthbook) {
          growthbook.setAttributes({
            id: uuid.getUuid()
          });
        }
      }
      if (path !== routerPath) {
        // on path change
        dispatch(initializePath(routerPath));
        setTimeout(() => {
          gtmBootstrap.performPageView(abta);
        }, 0);
      }
    }, [routerPath]);

    const gtmCookiesAccepted = new Cookies().get(Cookie.COOKIES_ACCEPTED);
    const cookiesAccepted = gtmCookiesAccepted === '1' ? true : gtmCookiesAccepted === '0' ? false : null;

    const appVariantId = EnvConfig.get().APP_VARIANT_ID;
    const localisations = { ...locales.labels, ...locales[appVariantId] };

    return (
      <StylesProvider>
        <Global styles={globalStyles(MuiTheme)} />
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={MuiTheme}>
            <WithT localeLabels={localisations}>
              <GoogleReCaptchaProvider reCaptchaKey={EnvConfig.get().GOOGLE_RE_CAPTCHA} scriptProps={{ defer: true }}>
                <ConditionalWrapper
                  condition={!isNull(growthbook)}
                  wrapper={(children) =>
                    growthbook ? (
                      <GrowthBookProvider growthbook={growthbook}>{children}</GrowthBookProvider>
                    ) : (
                      <>{children}</>
                    )
                  }
                >
                  <PersistGate persistor={persistor}>
                    {() => (
                      <>
                        <RootModalContainer />
                        <DevBanner />
                        <PageWrapper {...ownProps} Page={Page} />
                        <CookiesBanner cookiesAccepted={cookiesAccepted} isBookingPage={!!getBookingPageFromUrl()} />
                        <PaywallBannerModalContainer />
                        <EPCModalContainer tripType={ownProps.promoBannerTripType} />
                      </>
                    )}
                  </PersistGate>
                </ConditionalWrapper>
              </GoogleReCaptchaProvider>
            </WithT>
            <TalkdeskChatButton talkDeskConfig={TALKDESK_CONFIG} isProd={EnvConfig.isProd()} />
          </ThemeProvider>
        </StyledEngineProvider>
      </StylesProvider>
    );
  };
};
